import {Divider} from '@/components/ui/divider/Divider'
import {useTranslation} from '@/translations/i18n'
import {MedicalReportFieldsSchema, REPORT_TEXTAREA_LIMIT} from '../../formSchemas'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {MedicalReportTextArea} from '../medical-report-text-area/MedicalReportTextArea'
import {FormSection} from '@/components/commons/form-section/FormSection'
import {MedicalReportOperationalSelector} from '../medical-report-operational-selector/MedicalReportOperationalSelector'
import {z} from 'zod'
import {useGetPreviousReportByProject} from '../../queries/useGetPreviousReportByProject'

const NotesSchema = MedicalReportFieldsSchema.pick({
    notes: true,
    accesses: true,
    reportType: true,
    project: true
})
export type NotesSchema = z.infer<typeof NotesSchema>

export const TeamReportSections = () => {
    const form = useFormContext<NotesSchema>()
    const accesses = useWatch({control: form.control, name: 'accesses'})

    return (
        <>
            <OperationalSections isReadOnly={!!accesses} />
            <Divider direction="horizontal" />
            <NotesSections />
        </>
    )
}

const OperationalSections = ({isReadOnly = false}: {isReadOnly?: boolean}) => {
    const {t} = useTranslation()
    return (
        <FormSection title={t('medical_records:current_operators')}>
            <MedicalReportOperationalSelector isReadOnly={isReadOnly} />
        </FormSection>
    )
}

const NotesSections = () => {
    const {t} = useTranslation()
    const form = useFormContext<NotesSchema>()
    const projectId = useWatch({control: form.control, name: 'project.id'})
    const medicalReportTypeId = useWatch({control: form.control, name: 'reportType.id'})
    const previousProjectReportQuery = useGetPreviousReportByProject({
        projectId,
        medicalReportTypeId
    })

    const previousReport = previousProjectReportQuery?.data?.medicalRecordReportAttributes?.filter(
        attribute => attribute.name == 'notes'
    )?.[0]?.value

    return (
        <>
            <FormSection title={t('commons:report', {count: 1})}>
                <Controller
                    control={form.control}
                    name="notes"
                    render={({field, fieldState}) => (
                        <MedicalReportTextArea
                            rows={4}
                            maxLength={REPORT_TEXTAREA_LIMIT}
                            title={t('commons:report', {count: 1})}
                            placeholder={t('commons:description_dot')}
                            errorMessage={fieldState.error?.message}
                            currentField="notes"
                            onChange={field.onChange}
                            value={field.value}
                            previousReport={previousReport}
                        />
                    )}
                />
            </FormSection>
        </>
    )
}
